import React, { useState } from 'react';
import { Badge, Box, Button, Flex, SimpleGrid, Spinner, Text, useToast } from '@chakra-ui/react';
import { ArrowClockwise, FileDoc, Upload, Warning } from 'phosphor-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { ReturnButton } from '../../../components/buttons/ReturnButton';
import { GlassCard, GlassPageBg } from '../../../components/glass';
import { IData } from '../../../types/shared';
import {
  ITemplateSocietario,
  TipoOperacaoSocietario,
  useSocietario,
} from '../../../hooks/useSocietario';
import { AtualizarTemplateModal } from './components/AtualizarTemplateModal';
import { extractApiErrorMessage } from '../../../utils/ErrorUtils';

const tipoLabels: Record<TipoOperacaoSocietario, string> = {
  constituicao: 'Constituição',
  alteracao: 'Alteração contratual',
  transformacao: 'Transformação (EI → LTDA)',
};

const tipoDescricoes: Record<TipoOperacaoSocietario, string> = {
  constituicao: 'Template usado ao abrir uma nova sociedade',
  alteracao: 'Template usado em alterações contratuais',
  transformacao: 'Template usado na transformação de EI em LTDA',
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

interface TemplateCardProps {
  tipo: TipoOperacaoSocietario;
  templates: ITemplateSocietario[];
  isFetching: boolean;
  // eslint-disable-next-line no-unused-vars
  onAtualizar: (tipo: TipoOperacaoSocietario) => void;
}

const TemplateCard = ({ tipo, templates, isFetching, onAtualizar }: TemplateCardProps) => {
  const ativos = templates.filter(t => t.ativo);
  const ativo = ativos[0];
  const temMuitosAtivos = ativos.length > 1;

  let statusBlock: React.ReactNode;
  if (isFetching) {
    statusBlock = (
      <Flex align="center" gap={2} py={4}>
        <Spinner size="sm" />
        <Text fontSize="sm" color="gray.500">
          Carregando…
        </Text>
      </Flex>
    );
  } else if (ativo) {
    statusBlock = (
      <Box bg="gray.50" borderRadius="md" p={3} mb={3}>
        <Flex justify="space-between" align="center" mb={1}>
          <Text fontWeight="medium" fontSize="sm">
            {ativo.nome}
          </Text>
          <Badge colorScheme="green">Ativo</Badge>
        </Flex>
        <Text fontSize="xs" color="gray.600">
          Versão: <strong>{ativo.versao}</strong> · Criado em {formatDate(ativo.dt_criacao)}
        </Text>
        {ativo.descricao && (
          <Text fontSize="xs" color="gray.600" mt={1} noOfLines={2}>
            {ativo.descricao}
          </Text>
        )}
      </Box>
    );
  } else {
    statusBlock = (
      <Box bg="orange.50" borderRadius="md" p={3} mb={3}>
        <Flex align="center" gap={2}>
          <Warning size={18} color="#DD6B20" weight="duotone" />
          <Text fontSize="sm" color="orange.700" fontWeight="medium">
            Nenhum template cadastrado
          </Text>
        </Flex>
        <Text fontSize="xs" color="orange.600" mt={1}>
          Cadastre um template antes de gerar contratos deste tipo.
        </Text>
      </Box>
    );
  }

  return (
    <GlassCard
      intensity="strong"
      p={6}
      borderColor={temMuitosAtivos ? 'rgba(229, 62, 62, 0.45)' : undefined}
    >
      <Flex align="center" gap={3} mb={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="full"
          p={2}
          bg={tipo === 'constituicao' ? 'blue.50' : 'orange.50'}
        >
          <FileDoc
            size={24}
            color={tipo === 'constituicao' ? '#1976D2' : '#F2994A'}
            weight="duotone"
          />
        </Box>
        <Box>
          <Text fontWeight="bold" fontSize="md">
            {tipoLabels[tipo]}
          </Text>
          <Text fontSize="xs" color="gray.600">
            {tipoDescricoes[tipo]}
          </Text>
        </Box>
      </Flex>

      {statusBlock}

      {temMuitosAtivos && (
        <Box bg="red.50" borderRadius="md" p={3} mb={3}>
          <Flex align="center" gap={2}>
            <Warning size={18} color="#C53030" weight="duotone" />
            <Text fontSize="sm" color="red.700" fontWeight="medium">
              Mais de um template ativo ({ativos.length})
            </Text>
          </Flex>
          <Text fontSize="xs" color="red.600" mt={1}>
            A geração vai falhar até que sobre apenas um ativo. Atualize para substituir.
          </Text>
        </Box>
      )}

      <Button
        w="full"
        colorScheme="blue"
        leftIcon={ativo ? <ArrowClockwise size={16} /> : <Upload size={16} />}
        onClick={() => onAtualizar(tipo)}
      >
        {ativo ? 'Atualizar DOCX' : 'Cadastrar template'}
      </Button>
    </GlassCard>
  );
};

export const SocietarioTemplatesPage = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { getTemplates, createTemplate, deleteTemplate } = useSocietario();

  const [tipoAbrindo, setTipoAbrindo] = useState<TipoOperacaoSocietario | null>(null);

  const queryFilters = { size: 100 };
  const { data: templatesData, isFetching } = useQuery<IData<ITemplateSocietario[]>>({
    queryKey: ['societario-templates', queryFilters],
    queryFn: getTemplates,
  });

  const todos = templatesData?.data?.items ?? [];
  const constituicao = todos.filter(t => t.tipo_operacao === 'constituicao');
  const alteracao = todos.filter(t => t.tipo_operacao === 'alteracao');

  const createMutation = useMutation({ mutationFn: createTemplate });
  const deleteMutation = useMutation({ mutationFn: deleteTemplate });

  const handleSalvar = async (data: {
    nome: string;
    versao: string;
    descricao: string;
    file: File;
  }) => {
    if (!tipoAbrindo) return;
    const aRemover = todos.filter(t => t.tipo_operacao === tipoAbrindo);

    // Cria primeiro pra evitar janela "sem template ativo" se o create falhar.
    // Em caso de unique violation (versao reutilizada) o catch mostra erro e os
    // antigos ficam intactos. Soh apos sucesso do create soft-deletamos os
    // anteriores — perda parcial aqui resulta em multiplos ativos (cosmetico)
    // em vez de zero ativos (quebra a geracao do departamento inteiro).
    try {
      await createMutation.mutateAsync({
        nome: data.nome,
        versao: data.versao,
        descricao: data.descricao || null,
        tipo_operacao: tipoAbrindo,
        ativo: true,
        file: data.file,
      });
    } catch (err: any) {
      toast({
        status: 'error',
        title: 'Falha ao salvar template',
        description: extractApiErrorMessage(err),
      });
      return;
    }

    const removeResults = await Promise.allSettled(
      aRemover.map(t => deleteMutation.mutateAsync(t.id)),
    );
    const falhasRemocao = removeResults.filter(r => r.status === 'rejected').length;

    await queryClient.invalidateQueries({ queryKey: ['societario-templates'] });
    if (falhasRemocao > 0) {
      toast({
        status: 'warning',
        title: 'Template atualizado parcialmente',
        description: `Novo template ativo, mas ${falhasRemocao} versao(es) antiga(s) nao foram removidas. Remova manualmente.`,
      });
    } else {
      toast({ status: 'success', title: 'Template atualizado' });
    }
    setTipoAbrindo(null);
  };

  const templateAtualParaAbrir = (() => {
    if (!tipoAbrindo) return undefined;
    const lista = tipoAbrindo === 'constituicao' ? constituicao : alteracao;
    return lista.find(t => t.ativo);
  })();

  const isLoading = createMutation.isPending || deleteMutation.isPending;

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" px={{ base: 4, md: 6 }} py={6}>
        <ReturnButton previousPage="societario" label="Voltar" />
        <PageHeader
          title="Templates Societários"
          subtitle="Modelos DOCX versionados usados na geração de contratos. Um template ativo por tipo."
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mt={4}>
          <TemplateCard
            tipo="constituicao"
            templates={constituicao}
            isFetching={isFetching}
            onAtualizar={setTipoAbrindo}
          />
          <TemplateCard
            tipo="alteracao"
            templates={alteracao}
            isFetching={isFetching}
            onAtualizar={setTipoAbrindo}
          />
        </SimpleGrid>

        {tipoAbrindo && (
          <AtualizarTemplateModal
            isOpen={!!tipoAbrindo}
            onClose={() => setTipoAbrindo(null)}
            tipoOperacao={tipoAbrindo}
            templateAtual={templateAtualParaAbrir}
            onSubmit={handleSalvar}
            isLoading={isLoading}
          />
        )}
      </Box>
    </GlassPageBg>
  );
};

export default SocietarioTemplatesPage;
